import { type ReactNode, useContext, useEffect, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { CLEAR_FOCUS_EVENT } from '@react-aria/utils';

import { useSnapRegistry } from '../hooks/use-snap-registry';
import { resolveEmptyState } from '../types/command';
import type { RankedSection } from '../types/command-strategy';
import { hasAnyPendingCommand } from '../utils/has-any-pending-command';
import { selectCandidates } from '../utils/select-candidates';

import { CommandPaletteDerivedQueryContext } from './command-palette-derived-query-context';
import {
  LoadingEmptyState,
  NoMatchesEmptyState,
  RootEmptyState,
  SnapDiscoveryEmptyState,
} from './command-palette-empty-states';
import { useMainEmptyStates } from './command-palette-empty-states-context';
import { CommandPaletteResultsList } from './command-palette-results-list';
import { useCommandStrategy } from './command-palette-strategy-context';
import { useCommandRegistry } from './command-registry-provider';
import { useSnapPrefix } from './snap-prefix-context';

export interface CommandPaletteResultsProps {
  /**
   * Entered root id, or null in the main case. Branches the
   * candidate-selection scope and which empty-state variant the
   * component renders.
   */
  rootCommandId: string | null;
  /** Live in-mode query. */
  query: string;
  /** Activation handler (Enter, click). */
  onAction: (id: Key) => void;
}

/**
 * Single results orchestrator for both main mode and entered roots.
 * Owns the strategy invocation, the snap-prefix branch (main only),
 * the root-declared empty-state resolution (entered only), and the
 * generic fallback. Delegates listbox rendering to
 * `CommandPaletteResultsList`.
 *
 * Replaces the previous split between MainResults and EnteredResults.
 * The split had been doing one job -- forcing a remount of RAC's
 * ListBox when the user transitioned between main and entered modes
 * -- and we found that the remount isn't actually load-bearing once
 * the component owns the candidate-selection branch itself: RAC's
 * collection rebuilds from the new `items` array without needing a
 * fresh mount.
 *
 * Snap-prefix detection lives only in main mode. Inside an entered
 * root, typing `@flags` produces a literal search query, not a snap.
 *
 * Empty-state precedence:
 *   1. Snap-prefix mode + no matching snap -> discovery hint.
 *   2. Entered root with `status: 'pending'` + declared
 *      `loadingState` -> the consumer's loading copy.
 *   3. Entered root with a query-aware `emptyState` -> that.
 *   4. Main mode + anything pending -> provider's
 *      `mainLoadingState` resolver, falling back to generic
 *      "Searching\u2026".
 *   5. Main mode + no matches -> provider's `mainEmptyState`
 *      resolver, falling back to generic "No matches".
 */
export function CommandPaletteResults({ rootCommandId, query, onAction }: CommandPaletteResultsProps) {
  const { commands } = useCommandRegistry();
  const strategy = useCommandStrategy();
  const snapPrefix = useSnapPrefix();
  const snapRegistry = useSnapRegistry();
  const { mainEmptyState, mainLoadingState } = useMainEmptyStates();
  const derived = useContext(CommandPaletteDerivedQueryContext);
  const isSearchSettling = derived?.isSearchSettling ?? false;

  const isMainMode = rootCommandId === null;
  // Snap-prefix mode is detected purely on the input shape (prefix
  // + any text up to whitespace). Restricted to main mode -- inside
  // an entered root, `@flags` is just text the user typed.
  const isSnapPrefix = isMainMode && query.startsWith(snapPrefix) && !/\s/.test(query.slice(snapPrefix.length));

  const sections = useMemo<RankedSection[]>(() => {
    if (isSnapPrefix) {
      const needle = query.slice(snapPrefix.length).toLowerCase();
      const matched = Array.from(snapRegistry.entries())
        .filter(([key]) => key.toLowerCase().startsWith(needle))
        .map(([, command]) => command);
      if (matched.length === 0) {
        return [];
      }
      // One synthetic ungrouped section so row identity matches
      // what ResultsList renders elsewhere. Section headers would
      // duplicate the snap labels inline; flat reads cleaner.
      return [{ section: null, commands: matched }];
    }
    const candidates = selectCandidates(commands, rootCommandId, query);
    return strategy.rank(candidates, query);
  }, [isSnapPrefix, query, snapPrefix, snapRegistry, commands, rootCommandId, strategy]);

  const emptyState = useMemo<ReactNode>(() => {
    // Snap-prefix typed but no matching snap (e.g. `@xyz`). Only
    // possible in main mode (rootCommandId === null). Generic "No
    // matches" reads as if we ran a search; we didn't -- we
    // couldn't resolve the prefix. Surface one example snap.
    if (isSnapPrefix && query.length > snapPrefix.length && snapRegistry.size > 0) {
      const firstKey = snapRegistry.keys().next().value;
      if (firstKey !== undefined) {
        return <SnapDiscoveryEmptyState prefix={snapPrefix} firstKey={firstKey} />;
      }
    }
    // Inside an entered root: precedence is
    //   1. fetch in flight (status: 'pending') OR a debounced fetch
    //      imminent (isSearchSettling -- user typed, debounce window
    //      not yet elapsed) -> show loading copy, NOT a premature
    //      no-match. Without the settling guard the debounce window
    //      briefly falls through to the emptyState below, flashing
    //      "No ... match" for ~150ms before the fetch even starts.
    //      Prefer the scope's declared `loadingState`; fall back to a
    //      generic "Searching..." so even scopes without one don't
    //      flash. Skipped when the query is empty (settling is false
    //      and nothing is pending), so the empty-query teach state in
    //      the emptyState resolver still shows on bare scope entry.
    //   2. resolved emptyState -> the consumer's settled-state copy.
    //   3. generic "No matches".
    if (rootCommandId !== null) {
      const root = commands[rootCommandId];
      const rootIsPending = root !== undefined && root.type === 'action' && root.status === 'pending';
      if (root !== undefined && (rootIsPending || isSearchSettling)) {
        const loadingContent =
          root.type === 'action' && root.loadingState !== undefined ? root.loadingState : { label: 'Searching\u2026' };
        return <LoadingEmptyState content={loadingContent} />;
      }
      const content = root !== undefined ? resolveEmptyState(root.emptyState, { query }) : undefined;
      if (content !== undefined) {
        return <RootEmptyState content={content} />;
      }
    }
    // Main mode mirror of the scope-mode precedence: if anything
    // in the registry is loading, defer the "no matches" claim
    // until everything settles. Scopes can declare scope-specific
    // loading copy via `loadingState`; main mode aggregates across
    // many independent commands. The provider's `mainLoadingState`
    // resolver gets first crack at the copy; falling through to the
    // generic "Searching\u2026" message preserves the historical
    // default.
    // `isSearchSettling` is OR'd in so that while the user is typing
    // (debounce window not yet elapsed, no fetch started) we show the
    // loading copy instead of briefly flashing "No matches" before the
    // fetch even runs.
    if (rootCommandId === null && (hasAnyPendingCommand(commands) || isSearchSettling)) {
      const mainLoadingContent = mainLoadingState?.({ query });
      return <LoadingEmptyState content={mainLoadingContent ?? { label: 'Searching\u2026' }} />;
    }
    // Main-mode "no matches". The provider's `mainEmptyState`
    // resolver is the only path for consumer-supplied copy in main
    // mode; the per-command `emptyState` field is only consulted
    // inside an entered root. Returning `undefined` from the resolver
    // falls back to the palette's generic NoMatchesEmptyState.
    if (rootCommandId === null) {
      const mainEmptyContent = mainEmptyState?.({ query });
      if (mainEmptyContent !== undefined) {
        return <RootEmptyState content={mainEmptyContent} />;
      }
    }
    return <NoMatchesEmptyState />;
  }, [
    isSnapPrefix,
    query,
    snapPrefix,
    snapRegistry,
    commands,
    rootCommandId,
    mainEmptyState,
    mainLoadingState,
    isSearchSettling,
  ]);

  // Reset RAC's internal focus state when the user enters or exits
  // a scope. RAC's listbox keeps THREE focus-related state slots
  // that aren't auto-reconciled when the items prop swaps to a
  // disjoint collection:
  //
  //   1. `autocomplete.queuedActiveDescendant.current` (private
  //      ref) -- the id RAC's keydown handler routes events to.
  //   2. `autocomplete.state.focusedNodeId` -- drives the
  //      `aria-activedescendant` attribute on the input.
  //   3. selectable collection's `manager.focusedKey` -- drives
  //      `getKeyBelow` / `getKeyAbove` for ArrowDown / ArrowUp.
  //
  // On scope transitions, consumer code routinely unregisters
  // scope-children entirely (e.g. the `useQuery({ enabled })` gate
  // on search-flags). All three slots end up pointing at a node
  // that no longer exists. Symptoms:
  //   - ArrowDown silently no-ops because
  //     `getKeyBelow(staleKey)` returns null and
  //     `queuedActiveDescendant` points at a now-missing DOM id
  //     (RAC's keydown then dispatches the event to a null element).
  //   - `aria-activedescendant` continues to advertise the dead
  //     id to assistive tech.
  //
  // Mirror RAC's own `clearVirtualFocus()` reset sequence from the
  // outside:
  //
  //   a. Dispatch a synthetic `focusin` on the listbox itself.
  //      RAC's `updateActiveDescendant` listener detects the
  //      target === collection branch and clears both
  //      `queuedActiveDescendant.current` AND
  //      `state.focusedNodeId` (because `getElementById` returns
  //      null for the stale id).
  //   b. Dispatch `CLEAR_FOCUS_EVENT` (with `clearFocusKey: true`)
  //      on the listbox. `useSelectableCollection`'s listener
  //      clears `manager.focusedKey` and `manager.focused`.
  //
  // After both events, RAC is back to the "no row focused" state.
  // ArrowDown falls back to `getFirstKey()`; Enter is a no-op until
  // the user navigates (which is the documented RAC behaviour for
  // an autocomplete with no filter applied).
  //
  // This effect, the `key={rootCommandId}` on the listbox below,
  // AND the Autocomplete remount in CommandPaletteAutocomplete are
  // all load-bearing. The listbox key remounts the
  // SelectableCollection (clears manager.focusedKey naturally);
  // the effect clears autocomplete-level state that survives a
  // listbox remount when the option DOM id is stable across the
  // remount (collection id lives on Autocomplete); the Autocomplete
  // remount drops the delayed `setFocusedNodeId` timeout that this
  // effect cannot cancel from outside. Empirically: removing the
  // listbox key or this effect regresses arrow-key navigation after
  // entering an eagerly-populated scope; removing the Autocomplete
  // remount regresses Enter on scope re-entry under load.
  useEffect(() => {
    // The listbox lives inside the palette dialog. There's exactly
    // one of each on the page when this effect runs (the entire
    // dialog tree only mounts while the palette is open).
    const listbox = document.querySelector<HTMLElement>(
      '[role="dialog"][aria-label="Command palette"] [role="listbox"]',
    );
    if (listbox === null) {
      return;
    }
    // (a) Trip RAC's own self-cleanup of the autocomplete refs.
    listbox.dispatchEvent(new FocusEvent('focusin', { bubbles: true, cancelable: true }));
    // (b) Clear the selectable collection's focused key + focused state.
    listbox.dispatchEvent(
      new CustomEvent(CLEAR_FOCUS_EVENT, {
        bubbles: true,
        cancelable: true,
        detail: { clearFocusKey: true },
      }),
    );
  }, [rootCommandId]);

  // Focus-first on typing is RAC's built-in behaviour again. react-aria
  // 3.50 reworked auto-focus-first to set a pending flag on `beforeinput`
  // and consume it from a layout effect keyed on the collection's first
  // key/size -- i.e. it now waits for our externally-filtered results to
  // commit before pinning, which is exactly the race the old manual
  // FOCUS_EVENT re-pin effect here existed to avoid. (That manual dispatch
  // stopped working under 3.50 for the same reason: an event dispatched
  // after the collection settles is never consumed.)

  // Key on rootCommandId so the listbox re-mounts on scope
  // transitions. The previous architecture had a split between
  // MainResults and EnteredResults that did the same job implicitly
  // via component-type change; we merged them but lost the
  // load-bearing property. RAC's collection retains stale focus
  // state across items-prop changes; the manual reset above clears
  // autocomplete-level focusedNodeId, but the listbox's own
  // SelectableCollection state (manager.focusedKey) only resets
  // cleanly on a fresh mount. Both belt-and-suspenders are needed.
  //
  // Within a scope (typing to filter), no remount -- the items
  // prop still drives the collection rebuild on the existing
  // instance, so the keystroke-fast path is unaffected.
  return (
    <CommandPaletteResultsList
      key={rootCommandId ?? 'main'}
      sections={sections}
      emptyState={emptyState}
      onAction={onAction}
    />
  );
}
